import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AllowedGuard } from 'components/Tvendo/Common/UserAction';
import CardContainer from 'components/Tvendo/Common/CardContainer';
import RegularButton from 'components/CustomButtons/Button';
import { ConstantPermissions } from 'contexts/useConstant';
import GridContainer from 'components/Grid/GridContainer';
import { DataGrid, esES } from '@mui/x-data-grid';
import GridItem from 'components/Grid/GridItem';
import { Visibility } from '@material-ui/icons';
import { wsGetAll } from 'services/WService';
import { wsCreate } from 'services/WService';

const columns = [
  {
    field: 'DocEntry',
    width: 10,
    align: 'right',
    headerName: '#',
    disableColumnMenu: 100,
    hideable: false,
    sortable: false,
  },
  {
    field: 'Tipo',
    width: 100,
    align: 'center',
    headerName: 'Tipo',
    disableColumnMenu: 100,
    hideable: false,
    sortable: false,
    renderCell: ({ row }) => {
      return row.ObjType === '13' ? 'Documento' : row.ObjType === '14' ? 'NC' : 'Asiento';
    },
  },
  {
    field: 'SeriesStr',
    width: 70,
    headerName: 'Zona',
    hideable: false,
    sortable: false,
  },
  {
    field: 'U_SYP_MDTD',
    width: 100,
    headerName: 'T.Doc Sun.',
    hideable: false,
    sortable: false,
  },
  { field: 'U_SYP_MDSD', width: 100, headerName: 'Serie Doc.', hideable: false, sortable: false },
  { field: 'U_SYP_MDCD', width: 100, headerName: 'Corr. Sun.', hideable: false, sortable: false },
  { field: 'DocNum', width: 100, headerName: 'Num. SAP', hideable: false, sortable: false },
  {
    field: 'DocStatus',
    width: 100,
    headerName: 'Esta Doc.',
    disableColumnMenu: 100,
    hideable: false,
    sortable: false,
  },
  { field: 'TaxDate', width: 100, headerName: 'Fec. Doc.', hideable: false, sortable: false },
  { field: 'DocDueDate', width: 100, headerName: 'Fec. Vto.', hideable: false, sortable: false },
  { field: 'backDay', width: 100, headerName: 'Dias atrás', hideable: false, sortable: false },
  {
    field: 'DiscPrcnt',
    width: 70,
    align: 'right',
    headerName: '%Dsct.',
    hideable: false,
    sortable: false,
  },
  {
    field: 'DocTotal',
    width: 100,
    align: 'right',
    headerName: 'Total Doc.',
    hideable: false,
    sortable: false,
    renderCell: ({ row }) => {
      if(row.ObjType === 'Asiento'){
        return 0;
      }
      return parseFloat(row.DocTotal).toFixed(2);
    },
  },
  {
    field: 'PaidToDate',
    width: 100,
    align: 'right',
    headerName: 'Pagado',
    hideable: false,
    sortable: false,
    renderCell: ({ row }) => {
      if(row.ObjType === 'Asiento'){
        return 0;
      }
      return parseFloat(row.PaidToDate).toFixed(2);
    },
  },
  {
    field: 'Saldo',
    width: 100,
    align: 'right',
    headerName: 'Saldo',
    hideable: false,
    sortable: false,
    renderCell: ({ row }) => {
      if(row.ObjType === 'Asiento'){
        return parseFloat(row.Credit).toFixed(2);
      }
      return (row.DocTotal - row.PaidToDate).toFixed(2);
    },
  },
  {
    field: 'totalPaid',
    width: 150,
    align: 'right',
    type: 'number',
    editable: true,
    headerName: 'Pago T.',
    valueSetter: (params) => {
      params.row.totalPaid = parseFloat(params.value).toFixed(2);
      return params.row;
    },
  },
];

function NewReconciliation() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [documents, setDocuments] = useState([]);
  const [bp, setBp] = useState({});
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);

  async function listDocuments() {
    let date = searchParams.get('RefDate');
    let res = await wsGetAll(`pymt/reconciliation?CardCode=${id}&date=${date}`);
    if (res.success) {
      let docs = [];
      for (let doc of res.data) {
        docs.push(doc);
        docs.push({
          DocEntry: doc.DocEntry,
          ObjType: 'Asiento',
          SeriesStr: doc.SeriesStr,
          U_SYP_MDTD: '-',
          U_SYP_MDSD: '-',
          U_SYP_MDCD: '-',
          DocNum: doc.TransId,
          Credit: doc.Credit,
          TaxDate: doc.RefDate,
        });
      }
      setDocuments(docs);
      setBp(res.business_partner)
    }
  }

  async function createReconciliation() {
    setProcessing(true);
    let res = await wsCreate('pymt/reconciliation', {
      CardCode: id,
      date: searchParams.get('RefDate'),
    });
    setProcessing(false);
    if (res.success) {
      alert(res.message);
      navigate(`/admin/bancos/operaciones/reconciliacion`);
    } else {
      alert(res.message);
    }
  }

  useEffect(() => {
    listDocuments();
  }, []);

  return (
    <CardContainer title="Reconciliación pendientes">
      <GridContainer>
        <GridItem xs={5}>
          Cliente: {bp.CardCode || id}
          {!!bp.id && (
            <>
              {' '}
              - ${bp.CardName}
              <AllowedGuard permission={ConstantPermissions.BASE_BUSINESS_PARTNERS_SHOW}>
                <RegularButton
                  justIcon
                  color="primary"
                  round
                  simple
                  size="sm"
                  onClick={() => {
                    window.open(`/admin/socio-negocio/datos-maestros/clientes/${bp.id}`, '_blank');
                  }}
                >
                  <Visibility />
                </RegularButton>
              </AllowedGuard>
            </>
          )}
        </GridItem>
        <GridItem xs={5}>Fecha: {searchParams.get('RefDate')}</GridItem>
        <GridItem xs={2}>
          <RegularButton color="success" disabled={processing} onClick={createReconciliation}>
            Generar Reconciliación
          </RegularButton>
        </GridItem>
      </GridContainer>
      <GridContainer style={{ height: '600px' }}>
        <DataGrid
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          rowHeight={35}
          columns={columns}
          rows={documents}
          getRowId={(row) => {
            return `${row.ObjType}-${row.DocEntry}`;
          }}
          disableSelectionOnClick
        />
      </GridContainer>
    </CardContainer>
  );
}

export default NewReconciliation;
